import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export interface StepperFieldProps {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  width?: number | string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  dataTestId?: string;
}

const StepperField: React.FC<StepperFieldProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  width = 200,
  disabled = false,
  error = false,
  errorMessage,
  dataTestId,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isFloating = isFocused || value !== null;

  const increment = () => {
    if (disabled) {
      return;
    }
    const next = (value ?? 0) + step;
    if (max === undefined || next <= max) {
      onChange(next);
    }
  };

  const decrement = () => {
    if (disabled) {
      return;
    }
    const next = (value ?? 0) - step;
    if (min === undefined || next >= min) {
      onChange(next);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      onChange(null);
      return;
    }
    if (raw.replace(/[-.]/, '').length > 10) {
      return;
    }
    const num = Number(raw);
    if (!isNaN(num)) {
      if (max !== undefined && num > max) {
        return;
      }
      if (min !== undefined && num < min) {
        return;
      }
      onChange(num);
    }
  };

  return (
    <div
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      <div
        className={`relative flex items-center rounded-lg transition-colors duration-75 ${
          disabled
            ? 'bg-[#F6F6F6] cursor-not-allowed opacity-60'
            : 'bg-[#E5E7E9] hover:bg-neutral-85 cursor-text'
        }`}
        style={{
          height: 56,
          paddingLeft: 12,
          paddingRight: 4,
          borderBottom:
            isFocused && (value !== null || !error)
              ? '2px solid #2a4455'
              : error
                ? '2px solid #e11b1b'
                : 'none',
        }}
      >
        {/* Unfocused bottom border */}
        {!isFocused && !error && (
          <div
            className="absolute bottom-0"
            style={{ left: 6, right: 6, height: 1, backgroundColor: '#6a7278' }}
          />
        )}

        {/* Input area with floating label */}
        <div className="flex-1 relative flex flex-col justify-center py-2 min-w-0">
          <label
            className="absolute left-0 pointer-events-none origin-top-left"
            style={{
              color: isFocused ? '#2a4455' : '#6a7278',
              top: isFloating ? 4 : 16,
              fontSize: isFloating ? 12 : 16,
              lineHeight: '16px',
              transition:
                'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
            }}
          >
            {label}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={value !== null ? String(value) : ''}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            className="w-full bg-transparent outline-none text-base leading-6 text-neutral-10"
            style={{ fontSize: 16, lineHeight: '24px', paddingTop: 12 }}
            {...(dataTestId && { 'data-testid': dataTestId })}
          />
        </div>

        {/* Up / Down buttons */}
        <div className="flex flex-col shrink-0 self-stretch justify-center">
          <button
            type="button"
            onClick={increment}
            disabled={disabled || (max !== undefined && (value ?? 0) >= max)}
            className="flex items-center justify-center w-6 h-5 text-[#474A4C] hover:text-neutral-10 disabled:opacity-40 cursor-pointer"
            aria-label="Zwiększ"
          >
            <ChevronUpIcon className="w-4 h-4" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={decrement}
            disabled={disabled || (min !== undefined && (value ?? 0) <= min)}
            className="flex items-center justify-center w-6 h-5 text-[#474A4C] hover:text-neutral-10 disabled:opacity-40 cursor-pointer"
            aria-label="Zmniejsz"
          >
            <ChevronDownIcon className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Error message */}
      {errorMessage && (
        <p
          className="mt-1 px-4 text-xs text-error-40"
          style={{ fontSize: 12, lineHeight: '16px' }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default StepperField;
